/* eslint-disable @typescript-eslint/no-empty-function */
import styled from 'styled-components'
import { DashboardCard, P } from 'styling/GlobalStyles'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { IWidget } from 'types/dashboard.interface'
import { TitleText } from 'styling/Typography'
import { WidgetEditOverlay } from 'pages/Dashboard/Dashboard.styled'

type Department = {
  name: string
  present: number
  total: number
}

const DEPARTMENTS: Department[] = [
  { name: 'Mining Ops', present: 78, total: 85 },
  { name: 'Maintenance', present: 39, total: 42 },
  { name: 'Haulage', present: 30, total: 35 },
  { name: 'Processing', present: 27, total: 28 },
  { name: 'HSE', present: 14, total: 15 },
  { name: 'Admin', present: 10, total: 10 },
]

const HIGH_THRESHOLD = 95

/* ── Styled ── */

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const ViewAll = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colorOrange};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 3px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  gap: 10px;
`

const DeptRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`

const DeptName = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.greyText};
  min-width: 90px;
`

const DeptFraction = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.greyText};
  min-width: 44px;
  text-align: right;
`

const BarWrap = styled.div`
  flex: 1;
  position: relative;
  height: 3px;
  border-radius: 2px;
  overflow: hidden;
`

const BarTrack = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.textColor};
  opacity: 0.1;
`

const BarFill = styled.div<{ $color: string; $pct: number }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${(p) => p.$pct}%;
  max-width: 100%;
  background: ${(p) => p.$color};
  border-radius: 2px;
`

const DeptPct = styled.span<{ $color: string }>`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${(p) => p.$color};
  min-width: 46px;
  text-align: right;
`

/* ── Component ── */

export const HrByDepartmentWidget = ({
  modalOpen,
  removeWidget,
  onDragOver,
  onDragStart,
  onDragDrop,
}: IWidget) => {
  return (
    <DashboardCard
      draggable={modalOpen}
      onDragStartCapture={() => onDragStart('department', 1)}
      onDrop={onDragDrop}
      onDragEnd={(e) => e.preventDefault()}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver('department')
      }}
      width='1fr'
      style={{ gridArea: 'department', cursor: modalOpen ? 'grab' : 'normal' }}
    >
      {modalOpen && (
        <WidgetEditOverlay
          onClick={(e) => {
            removeWidget('department')
            e.stopPropagation()
          }}
        >
          <P>Remove Widget</P>
          <CloseIcon />
        </WidgetEditOverlay>
      )}

      <CardHeader>
        <TitleText weight='bold' style={{ margin: 0 }}>
          BY DEPARTMENT
        </TitleText>
        <ViewAll type='button'>
          View all <span aria-hidden='true'>{'>'}</span>
        </ViewAll>
      </CardHeader>

      <Content>
        {DEPARTMENTS.map((dept) => {
          const pct = dept.total > 0 ? Math.round((dept.present / dept.total) * 100) : 0
          const color = pct >= HIGH_THRESHOLD ? '#22c55e' : '#ff9900'
          return (
            <DeptRow key={dept.name}>
              <DeptName>{dept.name}</DeptName>
              <DeptFraction>
                {dept.present}/{dept.total}
              </DeptFraction>
              <BarWrap>
                <BarTrack />
                <BarFill $color={color} $pct={pct} />
              </BarWrap>
              <DeptPct $color={color}>{pct}%</DeptPct>
            </DeptRow>
          )
        })}
      </Content>
    </DashboardCard>
  )
}
